/**
 * Deep links from Telegram.
 *
 * An alert links to `/?ticker=BTCUSDT&tf=4h`, so one tap must land on that
 * exact chart. Applied once on mount, and only when the parameters name
 * something real -- a stale link to a delisted ticker should leave the last
 * selection alone rather than blanking the dashboard.
 *
 * The URL is then cleaned so a later reload does not re-apply an old link
 * over whatever the user has since selected.
 */

import { useEffect, useRef } from 'react'

import type { Symbol } from '@/api/types'
import { useAppStore } from '@/store/useAppStore'
import { TIMEFRAMES, type TimeframeKey } from '@/theme/chart'

export function useDeepLink(symbols: Symbol[] | undefined): void {
  const selectTicker = useAppStore((s) => s.selectTicker)
  const setTimeframe = useAppStore((s) => s.setTimeframe)
  const applied = useRef(false)

  useEffect(() => {
    if (applied.current || !symbols?.length) return

    const params = new URLSearchParams(window.location.search)
    const ticker = params.get('ticker')?.toUpperCase()
    const timeframe = params.get('tf')
    if (!ticker && !timeframe) {
      applied.current = true
      return
    }

    if (ticker && symbols.some((symbol) => symbol.ticker === ticker)) {
      selectTicker(ticker)
    }
    if (timeframe && (TIMEFRAMES as readonly string[]).includes(timeframe)) {
      setTimeframe(timeframe as TimeframeKey)
    }

    applied.current = true
    window.history.replaceState({}, '', window.location.pathname)
  }, [symbols, selectTicker, setTimeframe])
}
